import { api } from './client';

// ── Types ────────────────────────────────────────────────────
export interface Branch {
  _id: string;
  name: string;
  createdAt: string;
}

export type BranchRef = Branch | null;

export type EmployeeType = 'regular' | 'ex-employee' | 'Other DCs';

export interface Employee {
  _id: string;
  employeeId: number;
  name: string;
  title: string;
  canMakeInterview: boolean;
  employeeType: EmployeeType;
  mainArea: Area;
  areas: Area[] | string[];
  branch: Branch | null;
}

export interface Area {
  _id: string;
  name: string;
  employees: Employee[] | string[];
}

export interface DeviationType {
  _id: string;
  name: string;
  status: 'open' | 'in-progress' | 'closed';
  resolvedDate: string | null;
  createdAt: string;
}

export interface HercaRecord {
  _id: string;
  index: number;
  employeeId: Employee;
  incidentDate: string;
  interviewDate: string;
  description: string;
  deviationGapId: DeviationType;
  areaId: Area;
  interviewer: Employee;
  deviationRecordCode: number;
  createdAt: string;
}

// ── Employees ────────────────────────────────────────────────
export const addEmployee = (data: { employeeId: number; name: string; areaId: string; canMakeInterview?: boolean; employeeType?: EmployeeType; branchId?: string }) =>
  api.post<null>('/admin/new/employee', data);

export const updateEmployee = (id: string, data: { employeeId?: number; name?: string; title?: string; canMakeInterview?: boolean; employeeType?: EmployeeType; branchId?: string }) =>
  api.put<null>(`/admin/employee/${id}`, data);

export const deleteEmployee = (id: string) =>
  api.delete<null>(`/admin/employee/${id}`);

export const getEmployeeAreas = (employeeId: string) =>
  api.get<{ employeeId: string; name: string; areas: Area[] }>(`/admin/employee-areas/${employeeId}`);

// ── Areas ────────────────────────────────────────────────────
export const addArea = (data: { name: string }) =>
  api.post<null>('/admin/new/area', data);

export const getAreaEmployees = (areaId: string) =>
  api.get<{ areaId: string; name: string; employees: Employee[] }>(`/admin/area-employees/${areaId}`);

// ── Assign / Remove ──────────────────────────────────────────
export const assignEmployeeToArea = (employeeId: string, areaId: string) =>
  api.post<unknown>('/admin/assign-employee-to-area', { employeeId, areaId });

export const removeEmployeeFromArea = (employeeId: string, areaId: string) =>
  api.post<unknown>('/admin/remove-employee-from-area', { employeeId, areaId });

// ── Deviation Types ──────────────────────────────────────────
export const addDeviationType = (data: { name: string }) =>
  api.post<null>('/admin/new/deviation', data);

export const deleteDeviationType = (id: string) =>
  api.delete<null>(`/admin/deviation/${id}`);

// ── Herca Records ────────────────────────────────────────────
export const updateHerca = (id: string, data: {
  employeeId: string;
  incidentDate: string;
  interviewDate: string;
  description: string;
  deviationGapId: string;
  areaId: string;
  interviewer: string;
  deviationRecordCode?: number;
}) => api.put<null>(`/admin/herca/${id}`, data);

export const addHerca = (data: {
  employeeId: string;
  incidentDate: string;
  interviewDate: string;
  description: string;
  deviationGapId: string;
  areaId: string;
  interviewer: string;
  deviationRecordCode: number;
}) => api.post<null>('/admin/new/herca', data);

export const deleteHerca = (id: string) =>
  api.delete<null>(`/admin/herca/${id}`);

// ── Reports ──────────────────────────────────────────────────
export const getDeviationsAllTime = (areaId?: string) =>
  api.get<{
    totalDeviations: number;
    deviationsByArea: Record<string, number>;
    deviationsByEmployee: Record<string, string[]>;
    deviations: HercaRecord[];
  }>(`/admin/deviations-all-time${areaId ? `?areaId=${areaId}` : ''}`);

export const getDeviationsByPeriod = (startDate: string, endDate: string, areaId?: string) =>
  api.get<{
    period: { startDate: string; endDate: string };
    totalDeviations: number;
    deviationsByArea: Record<string, number>;
    deviationsByEmployee: Record<string, string[]>;
    deviations: HercaRecord[];
  }>(`/admin/deviations-by-period?startDate=${startDate}&endDate=${endDate}${areaId ? `&areaId=${areaId}` : ''}`);

export const getDeviationsByEmployee = (employeeId: string) =>
  api.get<{
    employeeId: string;
    employeeName: string;
    deviationCount: number;
    deviationsByArea: Record<string, HercaRecord[]>;
    deviations: HercaRecord[];
  }>(`/admin/deviations-by-employee/${employeeId}`);

export const getDeviationsByArea = (areaId: string) =>
  api.get<{
    areaId: string;
    areaName: string;
    deviationCount: number;
    deviations: HercaRecord[];
  }>(`/admin/deviations-by-area/${areaId}`);

// ── Branches ─────────────────────────────────────────────────
export const addBranch = (data: { name: string }) =>
  api.post<null>('/admin/new/branch', data);

export const updateBranch = (id: string, data: { name: string }) =>
  api.put<null>(`/admin/branch/${id}`, data);

export const deleteBranch = (id: string) =>
  api.delete<null>(`/admin/branch/${id}`);
